import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import api from "../lib/api";
import {
  Settings, Shield, Users, Calendar, Save,
  ArrowLeft, UserCheck, UserX, Clock, Crown, Lock, KeyRound
} from "lucide-react";

export default function AdminSettingsPage() {
  const { trainer, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [coachesSummary, setCoachesSummary] = useState(null);
  const [pwLoading, setPwLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm_password: "", otp: "" });
  const [settings, setSettings] = useState({
    beta_end_date: "2026-05-31",
    beta_client_limit: 10,
    pro_client_limit: 999,
    free_client_limit: 5,
    allow_new_registrations: true,
  });

  useEffect(() => {
    if (!isAdmin) {
      navigate("/dashboard", { replace: true });
      return;
    }
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const loadData = async () => {
    try {
      const [settingsRes, summaryRes] = await Promise.all([
        api.get("/admin/settings"),
        api.get("/admin/coaches-summary"),
      ]);
      setSettings((prev) => ({ ...prev, ...settingsRes.data }));
      setCoachesSummary(summaryRes.data);
    } catch (error) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        beta_end_date: settings.beta_end_date,
        beta_client_limit: settings.beta_client_limit,
        pro_client_limit: settings.pro_client_limit,
        free_client_limit: settings.free_client_limit,
        allow_new_registrations: settings.allow_new_registrations,
      });
      toast.success("Settings saved successfully");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-8">
      <Header subtitle="Admin Settings" />

      <main className="container mx-auto px-4 py-6 space-y-6 max-w-2xl">
        {/* Back button */}
        <Button
          variant="ghost"
          onClick={() => navigate("/admin")}
          className="text-muted-foreground hover:text-foreground gap-2"
          data-testid="back-to-admin-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Admin Dashboard
        </Button>

        {/* Page Title */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
            <Settings className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight uppercase text-foreground">
              Platform Settings
            </h1>
            <p className="text-sm text-muted-foreground">
              Manage platform configuration and coach limits
            </p>
          </div>
        </div>

        {/* Coach Summary Cards */}
        {coachesSummary && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Card className="glass-card border-0" data-testid="summary-total">
              <CardContent className="p-4 text-center">
                <Users className="w-5 h-5 text-primary mx-auto mb-2" />
                <p className="font-heading text-2xl font-bold text-foreground">
                  {coachesSummary.total}
                </p>
                <p className="text-xs text-muted-foreground">Total Coaches</p>
              </CardContent>
            </Card>
            <Card className="glass-card border-0" data-testid="summary-active">
              <CardContent className="p-4 text-center">
                <UserCheck className="w-5 h-5 text-neon-green mx-auto mb-2" />
                <p className="font-heading text-2xl font-bold text-neon-green">
                  {coachesSummary.active}
                </p>
                <p className="text-xs text-muted-foreground">Active</p>
              </CardContent>
            </Card>
            <Card className="glass-card border-0" data-testid="summary-suspended">
              <CardContent className="p-4 text-center">
                <UserX className="w-5 h-5 text-destructive mx-auto mb-2" />
                <p className="font-heading text-2xl font-bold text-destructive">
                  {coachesSummary.suspended}
                </p>
                <p className="text-xs text-muted-foreground">Suspended</p>
              </CardContent>
            </Card>
            <Card className="glass-card border-0" data-testid="summary-pending">
              <CardContent className="p-4 text-center">
                <Clock className="w-5 h-5 text-amber-400 mx-auto mb-2" />
                <p className="font-heading text-2xl font-bold text-amber-400">
                  {coachesSummary.pending}
                </p>
                <p className="text-xs text-muted-foreground">Pending</p>
              </CardContent>
            </Card>
            <Card className="glass-card border-0" data-testid="summary-beta">
              <CardContent className="p-4 text-center">
                <Shield className="w-5 h-5 text-amber-400 mx-auto mb-2" />
                <p className="font-heading text-2xl font-bold text-foreground">
                  {coachesSummary.beta_coaches}
                </p>
                <p className="text-xs text-muted-foreground">Beta Plan</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Beta Configuration */}
        <Card className="glass-card border-0" data-testid="beta-config-card">
          <CardHeader>
            <CardTitle className="font-heading text-lg tracking-wide uppercase flex items-center gap-2">
              <Calendar className="w-5 h-5 text-amber-400" />
              Beta Plan Configuration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-2">
              <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">
                Beta End Date
              </Label>
              <p className="text-base font-semibold text-foreground">31 May 2026</p>
              <p className="text-xs text-muted-foreground">
                Early beta coaches will keep their 10 client limit permanently.
              </p>
              <p className="text-xs text-muted-foreground">
                New coaches joining after the beta period will receive
                a free plan with up to 5 clients.
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">
                Beta Client Limit
              </Label>
              <p className="text-base font-semibold text-foreground">10</p>
              <p className="text-xs text-muted-foreground">
                Maximum number of clients allowed per beta coach.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Registration Settings */}
        <Card className="glass-card border-0" data-testid="registration-config-card">
          <CardHeader>
            <CardTitle className="font-heading text-lg tracking-wide uppercase flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              Registration Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label className="text-sm font-semibold text-foreground">
                  Allow New Registrations
                </Label>
                <p className="text-xs text-muted-foreground">
                  When disabled, no new coaches can sign up
                </p>
              </div>
              <Switch
                checked={settings.allow_new_registrations}
                onCheckedChange={(checked) =>
                  setSettings({ ...settings, allow_new_registrations: checked })
                }
                data-testid="allow-registrations-switch"
              />
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full h-14 btn-premium text-white font-semibold tracking-wide uppercase rounded-xl"
          data-testid="save-settings-btn"
        >
          {saving ? (
            <div className="spinner" />
          ) : (
            <>
              <Save className="w-5 h-5 mr-2" />
              Save Settings
            </>
          )}
        </Button>

        {/* Admin Password Change with OTP */}
        <Card className="glass-card mt-6">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Lock className="w-5 h-5 text-primary" />
              Change Admin Password
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Current Password</Label>
              <Input type="password" value={pwForm.current_password}
                onChange={(e) => setPwForm(p => ({...p, current_password: e.target.value}))}
                placeholder="Enter current password"
                className="bg-white/[0.03] border-white/[0.06]"
                data-testid="admin-current-pw" />
            </div>
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input type="password" value={pwForm.new_password}
                onChange={(e) => setPwForm(p => ({...p, new_password: e.target.value}))}
                placeholder="Enter new password"
                className="bg-white/[0.03] border-white/[0.06]"
                data-testid="admin-new-pw" />
            </div>
            <div className="space-y-2">
              <Label>Confirm New Password</Label>
              <Input type="password" value={pwForm.confirm_password}
                onChange={(e) => setPwForm(p => ({...p, confirm_password: e.target.value}))}
                placeholder="Confirm new password"
                className="bg-white/[0.03] border-white/[0.06]"
                data-testid="admin-confirm-pw" />
            </div>

            <div className="p-4 rounded-xl bg-primary/5 border border-primary/20 space-y-3">
              <div className="flex items-center gap-2 text-sm text-primary">
                <Shield className="w-4 h-4" />
                <span className="font-semibold uppercase tracking-wide">Email OTP Verification</span>
              </div>
              {!otpSent ? (
                <Button onClick={async () => {
                  setOtpLoading(true);
                  try { const r = await api.post('/auth/send-password-otp'); toast.success(r.data.message); setOtpSent(true); }
                  catch(e) { toast.error(e.response?.data?.detail || "Failed to send OTP"); }
                  finally { setOtpLoading(false); }
                }} disabled={otpLoading} variant="outline"
                  className="w-full h-10 border-primary/30 text-primary hover:bg-primary/10 font-semibold tracking-wide uppercase text-xs rounded-xl"
                  data-testid="admin-send-otp-btn">
                  {otpLoading ? <div className="spinner-sm" /> : <><KeyRound className="w-4 h-4 mr-2" />Send OTP to Email</>}
                </Button>
              ) : (
                <div className="space-y-2">
                  <Input type="text" inputMode="numeric" maxLength={6} value={pwForm.otp}
                    onChange={(e) => setPwForm(p => ({...p, otp: e.target.value.replace(/\D/g, '')}))}
                    placeholder="6-digit OTP" className="bg-white/[0.03] border-primary/30 text-center text-lg tracking-[0.5em] font-mono"
                    data-testid="admin-otp-input" />
                  <button onClick={async () => {
                    setOtpLoading(true);
                    try { const r = await api.post('/auth/send-password-otp'); toast.success(r.data.message); }
                    catch(e) { toast.error("Failed"); }
                    finally { setOtpLoading(false); }
                  }} disabled={otpLoading} className="text-xs text-primary hover:underline"
                    data-testid="admin-resend-otp-btn">{otpLoading ? "Sending..." : "Resend OTP"}</button>
                </div>
              )}
            </div>

            <Button onClick={async () => {
              if (!pwForm.current_password || !pwForm.new_password) { toast.error("Fill all fields"); return; }
              if (pwForm.new_password !== pwForm.confirm_password) { toast.error("Passwords don't match"); return; }
              if (pwForm.new_password.length < 6) { toast.error("Min 6 characters"); return; }
              if (!pwForm.otp) { toast.error("Enter OTP"); return; }
              setPwLoading(true);
              try {
                await api.post('/auth/change-password', { current_password: pwForm.current_password, new_password: pwForm.new_password, otp: pwForm.otp });
                toast.success("Password changed!");
                setPwForm({ current_password: "", new_password: "", confirm_password: "", otp: "" });
                setOtpSent(false);
              } catch(e) { toast.error(e.response?.data?.detail || "Failed"); }
              finally { setPwLoading(false); }
            }} disabled={pwLoading || !otpSent} className="w-full h-11 btn-premium text-white font-semibold tracking-wide uppercase rounded-xl"
              data-testid="admin-change-pw-btn">
              {pwLoading ? <div className="spinner-sm" /> : "Change Password"}
            </Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
